import axios from 'axios'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001'
})

// Request map to track cancel tokens
const cancelTokenMap = new Map();

api.interceptors.request.use(config => {
  const source = axios.CancelToken.source();
  config.cancelToken = source.token;
  cancelTokenMap.set(config.url, source);

  const token = localStorage.getItem('token');
  if (!token) {
    source.cancel('No auth token');
    window.location.href = '/login';
    return config;
  }
  
  config.headers.Authorization = `Bearer ${token}`;
  // Disable caching by adding a no-cache header
  config.headers['Cache-Control'] = 'no-cache';
  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    if (axios.isCancel(error)) {
      return Promise.reject({ isCanceled: true });
    }
    
    if (error.response?.status === 401) {
      cancelTokenMap.forEach(source => source.cancel('Unauthorized'));
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

export const getRequests = (offset = 0) => 
  // Add a timestamp query parameter to bust caches
  api.get(`/requests?offset=${offset}&_ts=${new Date().getTime()}`)
    .then(res => res.data)
    .catch(err => {
      throw new Error(err.response?.data?.message || 'Failed to fetch requests');
    });

export const getConversions = (offset = 0) => 
  api.get(`/conversions?offset=${offset}&_ts=${new Date().getTime()}`)
    .then(res => res.data)
    .catch(err => {
      throw new Error(err.response?.data?.message || 'Failed to fetch conversions');
    });

export const getDomains = () => 
  api.get('/domains')
    .then(res => res.data)
    .catch(err => {
      throw new Error(err.response?.data?.error || 'Failed to fetch domains');
    });

export const addDomain = (domain) =>
  api
    .post('/domains', { domain })
    .then(res => res.data)
    .catch(err => {
      // normalize the error so the React component can still do error.response.data.error
      const data = err.response?.data || {}
      const e = new Error(data.error || 'Failed to add domain')
      e.response = { data, status: err.response?.status }
      throw e
    })    

export const deleteDomain = (domain) => 
  api.delete(`/domains/${domain}`)
    .catch(err => {
      throw new Error(err.response?.data?.error || 'Failed to delete domain');
    });

export const getTotals = (type) => 
  api.get(`/totals?type=${type}`)
    .then(res => res.data)
    .catch(err => {
      throw new Error(err.response?.data?.message || `Failed to fetch ${type} totals`);
    });

export const getDailyTotals = (type) => 
  api.get(`/totals/daily?type=${type}`)
    .then(res => res.data)
    .catch(err => {
      throw new Error(err.response?.data?.message || `Failed to fetch daily ${type} totals`);
    });
